// What the till knows about itself and whoever is standing at it.
//
// Both answers come from the server, because both cookies are HttpOnly: the
// browser cannot read them, so asking is the only honest way to know. Two
// questions, because they fail differently -- a till that is not paired needs
// the pairing screen, and a paired till with nobody at it needs the lock.

import { api } from '../api/client';
import type { components } from '../api/types';

export type Identity = components['schemas']['TillIdentityBody'];
export type Session = components['schemas']['SessionBody'];

export type TillState =
	| { status: 'loading' }
	| { status: 'unpaired' }
	| { status: 'locked'; till: Identity }
	| { status: 'open'; till: Identity; session: Session }
	| { status: 'failed' };

let state = $state<TillState>({ status: 'loading' });

export function till(): TillState {
	return state;
}

/** Ask the server which till this is and who is at it. */
export async function loadTill(): Promise<void> {
	let identity: Identity;
	try {
		const me = await api.GET('/api/till/me');
		if (me.response.status === 401) {
			state = { status: 'unpaired' };
			return;
		}
		if (me.error !== undefined || !me.data) {
			state = { status: 'failed' };
			return;
		}
		identity = me.data;
	} catch {
		// A fetch that never reaches the server throws rather than returning an
		// error field. Without this the promise rejects, nothing sets a state,
		// and the till sits on "Loading" for ever.
		state = { status: 'failed' };
		return;
	}

	try {
		const who = await api.GET('/api/till/sessions/me');
		if (who.error !== undefined || !who.data) {
			// Paired, nobody signed in. Not a failure: it is the state a till
			// spends most of the night in.
			state = { status: 'locked', till: identity };
			return;
		}
		state = { status: 'open', till: identity, session: who.data };
	} catch {
		state = { status: 'locked', till: identity };
	}
}

/** Record a pairing that has just succeeded, with no second round trip. */
export function paired(identity: Identity): void {
	state = { status: 'locked', till: identity };
}

/** Record an unlock, and take the chance to re-read the settings.
 *
 * The identity carries the account's language and idle timeout, and a till
 * reads it at boot -- so a kiosk tab open for days would otherwise keep the
 * settings it started with. Unlocking happens many times a day on a real till,
 * which makes it the natural moment to catch up. The re-read is fire and
 * forget: a failure leaves the settings a cashier is already working under,
 * which is the safe way to be wrong.
 */
export function opened(session: Session): void {
	if (state.status === 'locked' || state.status === 'open') {
		state = { status: 'open', till: state.till, session };
		void refreshTill();
	}
}

/** Re-read the till's identity, keeping whoever is signed in. */
async function refreshTill(): Promise<void> {
	try {
		const me = await api.GET('/api/till/me');
		if (me.error !== undefined || !me.data) return;
		if (state.status === 'open') {
			state = { status: 'open', till: me.data, session: state.session };
		} else if (state.status === 'locked') {
			state = { status: 'locked', till: me.data };
		}
	} catch {
		// Keeping the settings in hand is the safe way to be wrong.
	}
}

/** Record a sign-out: the till stays paired. */
export function closed(): void {
	if (state.status === 'open') {
		state = { status: 'locked', till: state.till };
	}
}

/** Re-read the settings after an unlock that did not change who is signed in,
 * which is what clearing the lock overlay is. */
export function unlocked(): void {
	void refreshTill();
}
